import { action, computed, IObservableArray, makeObservable, observable, reaction, runInAction } from 'mobx';
import { ApiReq, emptyValue } from 'src/api';
import { ClickOnLegendType, ClickOnPointType, ExportCallBackType, ExportTypes, ItemType } from 'src/types';

import { storeFactory } from '../../utils/store';

import {
  EarLocationsRequestParams,
  GeneratorItem,
  GeneratorsFilterType,
  GeneratorsResponse,
} from 'src/api/api-types/generators';
import { GeneratorModification } from 'src/models/generators/generator-modification';
import { Generator } from '../../models/generators/generator';

import { EarDiagramsApi } from '../../api/diagrams';
import {
  CardType,
  CompliantContainersResponse,
  DiagramsCardsResponse,
  LineChartsType,
  PieChartsResponse,
} from '../../api/api-types/diagrams';
import { lookupsStore } from '../../stores/lookups';
import { DashboardSection, Sections } from 'src/models/SectionsOfDashboard';
import { ExportDataType, ExportDashboardFormType } from 'src/api/exportTypes';
import { GridRowId } from '@mui/x-data-grid-pro';
import { exportOptions, generateFilterTitle } from './helpers/common';
import { TableFilter } from 'src/models/filter/table-filter';
import { columns } from '../Generators/helpers/gridColumns';
import { GeneratorExportColumnsNameMap, generatorsFilterNameMap } from '../../models/filter/constants';
import {
  defaultGeneratorSearch,
  visibilityDefaultState,
} from '../Generators/helpers/common';
import { MergedLookup } from 'src/api/api-types/lookups';
import { TablePagination } from '../../models/pagination/pagination';
import { GridColumnsManager } from '../../models/grid-columns-manager/grid-columns-manager';
import { StockChart } from '../../models/charts/StockChart';
import { MultiStockChart } from '../../models/charts/MultiStockChart';
import { PieChart } from '../../models/charts/PieChart';
import { PolarChart } from '../../models/charts/PolarChart';
import { slcpCompliancePieColors, generatorsNotCompliantByContainerPieColors } from './components/Charts/config/constant';
import { ignoreType } from './components/Cards/utils';
import { CardsType } from 'src/utils/constants';
import { AsyncExportEntity } from 'src/models/export/AsyncExportEntity';
import { DashboardPersistenceManager, DashboardPersistedState } from 'src/utils/persistence/DashboardPersistenceManager';
import { dotNameKeyToObject, removeEmptyValues } from '../../utils/string';
import { authStore } from 'src/stores/auth';
import { format } from 'date-fns';

export type GeneratorsLookUpsType = Partial<
  Record<keyof GeneratorItem, Array<ItemType | MergedLookup>>
>

export type FilterSourceType = 'ear' | 'kpi' | 'topCards' | null;
export type ComplianceModeType = 'notCompliant' | 'compliant' | 'not-compliant-by-container';

export class DashboardStore {
  diagramApi = new EarDiagramsApi({ prefix: '' });

  @observable targetJurisdiction?: ItemType;
  @observable cardFilterTitle?: string;
  @observable selectedGenerators: Array<GridRowId> = [];
  @observable sections = new Sections();
  @observable stockChartOptions?: StockChart;
  @observable multiChartOptions?: MultiStockChart;
  @observable pieChartOptions?: PieChart;
  @observable polarChartOptions?: PolarChart;
  @observable compliancePieChartOptions?: PieChart;
  @observable generatorsNotCompliantByContainerPieChartOptions?: PieChart;
  @observable exportEntity: AsyncExportEntity = new AsyncExportEntity();

  @observable.ref pieCollectionSysEARReq: ApiReq<PieChartsResponse> = emptyValue;
  @observable.ref pieMaterialKPIReq: ApiReq<PieChartsResponse> = emptyValue;
  @observable.ref pieSecondMaterialKPIReq: ApiReq<PieChartsResponse> = emptyValue;
  @observable.ref pieGeneratorsNotCompliantByContainerPieChartReq: ApiReq<PieChartsResponse> = emptyValue;

  @observable.ref linerComplianceEARReq: ApiReq<LineChartsType> = emptyValue;
  @observable.ref recyclingKPIReq: ApiReq<LineChartsType> = emptyValue;

  @observable.ref cardsCommonReq: ApiReq<DiagramsCardsResponse> = emptyValue;
  @observable.ref cardsGeneralReq: ApiReq<DiagramsCardsResponse> = emptyValue;
  @observable.ref cardsMoreReq: ApiReq<DiagramsCardsResponse> = emptyValue;
  @observable.ref cardsMcrReq: ApiReq<DiagramsCardsResponse> = emptyValue;

  @observable.ref generatorsReq: ApiReq<GeneratorsResponse> = emptyValue;
  @observable.ref generators: IObservableArray<Generator> = observable([]);
  @observable.ref chosenGenerator?: GeneratorModification;

  @observable dashboardExportEntity: AsyncExportEntity = new AsyncExportEntity();

  @observable pagination = new TablePagination<GeneratorsResponse>({
    requestRef: this.generatorsReq,
  });

  @observable columnsManager;
  @observable filterModel?: TableFilter<GeneratorsFilterType, GeneratorItem>;
  @observable filterSource: FilterSourceType = null;
  @observable selectedGeneratorTypesEAR: string[] = [];
  @observable selectedGeneratorTypesKPI: string[] = [];
  @observable selectedPolarChartSector: string | null = null;
  @observable selectedPointTimestamp: number | null = null;
  @observable selectedFilterId: string | null = null;
  @observable complianceMode: ComplianceModeType = 'compliant';

  @observable.ref compliantContainersPopoverElement?: HTMLElement | SVGElement;
  @observable.ref compliantContainersReq: ApiReq<CompliantContainersResponse> = emptyValue;
  @observable compliantContainersFilterId: string | null = null;

  constructor() {
    makeObservable(this);

    this.pagination.setFetchingCallBack(this.fetchLocations);
    this.columnsManager = new GridColumnsManager<GeneratorItem>({
      columns,
      columnsMap: this.columnsVisibleMap,
      visibilityModel: visibilityDefaultState,
    });

    reaction(() => this.dashboardExportEntity.loading, () => {
      if (!this.dashboardExportEntity.loading) {
        const charts = [
          this.stockChartOptions,
          this.multiChartOptions,
          this.pieChartOptions,
          this.compliancePieChartOptions,
          this.generatorsNotCompliantByContainerPieChartOptions,
          this.polarChartOptions,
        ];
        charts.forEach(chartOptions =>
          chartOptions?.chartRef?.loadingShown && chartOptions?.chartRef?.hideLoading(),
        );
      }
    });
  }

  @computed get columnsVisibleMap() {
    return {
      jurisdiction: {
        jurisdiction: false,
      },
    };
  }

  @computed get filter() {
    this.filterModel ??= new TableFilter<GeneratorsFilterType, GeneratorItem>(
      this.columnsManager?.gridColumns,
      defaultGeneratorSearch,
      generatorsFilterNameMap,
      (column, filters) => {
        const additionalFilters = this.generatorsAdditionalFilter;
        return this.diagramApi.getGeneratorsFilterOptions({ filters, additionalFilters, column });
      }
    );
    return this.filterModel;
  }

  @computed get generatorsData() {
    if (this.generatorsReq.state !== 'fulfilled' || !this.generatorsReq.value) {
      return [];
    }
    return this.generatorsReq.value.data?.items || [];
  }

  @computed get totalGenerators() {
    if (this.generatorsReq.state !== 'fulfilled' || !this.generatorsReq.value) {
      return undefined;
    }
    return this.generatorsReq.value.data?.totalGenerators;
  }

  @computed get commonCards() {
    if (this.cardsCommonReq.state !== 'fulfilled' || !this.cardsCommonReq.value) {
      return [];
    }
    return this.cardsCommonReq.value.data?.cards || [];
  }

  @computed get generalCards() {
    if (this.cardsGeneralReq.state !== 'fulfilled' || !this.cardsGeneralReq.value) {
      return [];
    }
    const cards = this.cardsGeneralReq.value.data?.cards || [];
    // Clean titles by removing (%)
    return cards.map(card => ({
      ...card,
      title: card.title.replace(/\s*\(%\)\s*/g, '').trim(),
    }));
  }

  @computed get mcrCards() {
    if (this.cardsMcrReq.state !== 'fulfilled' || !this.cardsMcrReq.value) {
      return [];
    }
    return this.cardsMcrReq.value.data?.cards || [];
  }

  @computed get moreCards() {
    if (this.cardsMoreReq.state !== 'fulfilled' || !this.cardsMoreReq.value) {
      return [];
    }
    return this.cardsMoreReq.value.data?.cards || [];
  }

  @computed get mcrCardsFormatted() {
    return this.mcrCards.map((card) => {
      const match = card.title.match(/^\(([^)]+)\)\s*/);
      return {
        ...card,
        cardTitle: match ? match[1].trim() : 'MCR',
        subtitle: match ? card.title.replace(match[0], '').trim() : card.title.trim(),
      };
    });
  }

  @computed get moreCardsFormatted() {
    return this.moreCards.map((card) => {
      const match = card.title.match(/^\(([^)]+)\)\s*/);
      return {
        ...card,
        cardTitle: match ? match[1].trim() : 'MORe',
        subtitle: match ? card.title.replace(match[0], '').trim() : card.title.trim(),
      };
    });
  }

  @computed get pieCollectionSysEARData() {
    if (this.pieCollectionSysEARReq.state !== 'fulfilled' || !this.pieCollectionSysEARReq.value) {
      return null;
    }
    return this.pieCollectionSysEARReq.value.data?.pieChart;
  }

  @computed get pieMaterialKPIData() {
    if (this.pieMaterialKPIReq.state !== 'fulfilled' || !this.pieMaterialKPIReq.value) {
      return null;
    }
    return this.pieMaterialKPIReq.value.data?.pieChart;
  }

  @computed get isPieMaterialKPIEmpty() {
    if (!this.pieMaterialKPIData) return false;
    const res = this.pieMaterialKPIData.values.reduce((acc: any, cur: any) => {
      return acc + Number(cur.value);
    }, 0);

    return res !== 0;
  }

  @computed get isPieCollectionSysEAREmpty() {
    if (!this.pieCollectionSysEARData) return false;
    const res = this.pieCollectionSysEARData.values.reduce((acc: any, cur: any) => {
      return acc + Number(cur.value);
    }, 0);
    return res !== 0;
  }

  @computed get slcpCompliancePieChartData() {
    if (this.pieSecondMaterialKPIReq.state !== 'fulfilled' || !this.pieSecondMaterialKPIReq.value) {
      return null;
    }
    return this.pieSecondMaterialKPIReq.value.data?.pieChart;
  }

  @computed get generatorsNotCompliantByContainerPieChartData() {
    if (this.pieGeneratorsNotCompliantByContainerPieChartReq.state !== 'fulfilled' || !this.pieGeneratorsNotCompliantByContainerPieChartReq.value) {
      return null;
    }
    return this.pieGeneratorsNotCompliantByContainerPieChartReq.value.data?.pieChart;
  }

  @computed get linerComplianceEARData() {
    if (this.linerComplianceEARReq.state !== 'fulfilled' || !this.linerComplianceEARReq.value) {
      return null;
    }
    return this.linerComplianceEARReq.value.data?.lineChart;
  }

  @computed get recyclingKPIData() {
    if (this.recyclingKPIReq.state !== 'fulfilled' || !this.recyclingKPIReq.value) {
      return null;
    }
    return this.recyclingKPIReq.value.data?.lineChart;
  }

  @computed get requestGeneratorsFilter() {
    return {
      ...this.filter.filterParams,
      ...(this.targetJurisdiction?.id && { 'q.jurisdiction.id': String(this.targetJurisdiction?.id) || null }),
    };
  }

  @computed get selectedGeneratorTypes(): string[] {
    return this.filterSource === 'kpi'
      ? this.selectedGeneratorTypesKPI
      : this.selectedGeneratorTypesEAR;
  }

  @computed get isEARFilterActive() {
    return !this.isAllGeneratorTypesSelectedEAR;
  }

  @computed get isKPIFilterActive() {
    return !this.isAllGeneratorTypesSelectedKPI;
  }

  @computed get displaySelectedGeneratorTypes(): string[] {
    const types = this.selectedGeneratorTypes;
    if (types.length === 0) {
      return this.earGeneratorTypes.map(type => String(type.id));
    }
    return types;
  }

  @computed get isAllGeneratorTypesSelected(): boolean {
    const types = this.selectedGeneratorTypes;
    return types.length === 0 ||
      types.length === this.earGeneratorTypes.length;
  }

  @computed get isAllGeneratorTypesSelectedEAR(): boolean {
    return this.selectedGeneratorTypesEAR.length === 0 ||
      this.selectedGeneratorTypesEAR.length === this.earGeneratorTypes.length;
  }

  @computed get isAllGeneratorTypesSelectedKPI(): boolean {
    return this.selectedGeneratorTypesKPI.length === 0 ||
      this.selectedGeneratorTypesKPI.length === this.earGeneratorTypes.length;
  }

  @computed get earGeneratorTypes(): ItemType[] {
    return lookupsStore.locationTypes
      // Commercial, Multifamily Commercial, Residential, Residential
      // Generators only
      .filter(x => [1, 2, 5, 6].includes(x.id));
  }

  @computed get generatorsAdditionalFilter(): EarLocationsRequestParams | null {
    if (!authStore.fCfg?.isEARGeneratorTypeFiltering) {
      return null;
    }
    const additionalFilters: EarLocationsRequestParams = {};
    if (this.selectedFilterId) {
      additionalFilters.filter = { id: Number(this.selectedFilterId) };
    }
    if (this.requestGeneratorTypes.length) {
      additionalFilters.types = this.requestGeneratorTypes.map(x => ({ id: Number(x) }));
    }
    if (this.selectedPointTimestamp) {
      additionalFilters.date = format(this.selectedPointTimestamp, 'yyyy-MM-dd');
    }

    if (!Object.getOwnPropertyNames(additionalFilters).length) {
      return null;
    }

    return additionalFilters;
  }

  @computed get compliantContainers() {
    if (this.compliantContainersReq.state !== 'fulfilled' || !this.compliantContainersReq.value) {
      return null;
    }
    return this.compliantContainersReq.value.data;
  }

  // Default EAR types for card clicks (Commercial, Multifamily Commercial)
  get defaultEarTypes(): string[] {
    return ['1', '2'];
  }

  @action setFilterSource = (source: FilterSourceType) => {
    this.filterSource = source;
  };

  @action setSelectedGeneratorTypes = (types: string[]) => {
    if (this.filterSource === 'kpi') {
      this.selectedGeneratorTypesKPI = types;
    } else {
      this.selectedGeneratorTypesEAR = types;
    }
  };

  @action applyGeneratorTypesFilter = async (types: string[]) => {
    // Reset selected section when changing generator types
    this.selectedFilterId = null;
    this.selectedPointTimestamp = null;
    this.selectedPolarChartSector = null;
    this.resetCardFilterTitle();
    this.resetChartSelection();
    this.filter.resetFilters();

    if (this.filterSource === 'kpi') {
      this.selectedGeneratorTypesKPI = types;
      // Reset EAR types when selecting KPI types
      this.saveToStorage();
      // Refresh KPI charts: line chart + active pie chart
      await Promise.allSettled([
        this.fetchRecyclingKPI(),
        ({
          'compliant': this.fetchSlcpCompliancePieChart,
          'notCompliant': this.fetchPieMaterialKPI,
          'not-compliant-by-container': this.fetchGeneratorsNotCompliantByContainerPieChart
        } as Record<ComplianceModeType, () => Promise<void>>)[this.complianceMode]()
      ]);

    } else {
      this.selectedGeneratorTypesEAR = types;
      this.saveToStorage();
      // Refresh Collection Systems chart and table
      await Promise.allSettled([
        this.fetchPieCollectionSysEAR(),
      ]);
    }
  };

  @action selectAllGeneratorTypes = async () => {
    if (this.earGeneratorTypes.length === 0) {
      await lookupsStore.fetchLocationTypes();
    }

    runInAction(() => {
      const allTypes = this.earGeneratorTypes.map(type => String(type.id));
      this.selectedGeneratorTypesEAR = allTypes;
      this.selectedGeneratorTypesKPI = allTypes;
    });
  };

  @action setJurisdictions = (value: ItemType) => {
    this.targetJurisdiction = value;
  };

  @action setCardFilterTitle = (title?: string) => this.cardFilterTitle = title;
  @action fetchCommonCards = async (id?: number) => {
    this.cardsCommonReq = this.diagramApi.getCommonCards(id, this.defaultEarTypes);
    await this.cardsCommonReq;
    return this.generatorsReq;
  };

  @action fetchMcrCards = async (id?: number) => {
    this.cardsMcrReq = this.diagramApi.getMcrCards(id, this.defaultEarTypes);
    await this.cardsMcrReq;
    return this.cardsMcrReq;
  };
  @action fetchMoreCards = async (id?: number) => {
    this.cardsMoreReq = this.diagramApi.getMOReCards(id, this.defaultEarTypes);
    await this.cardsMoreReq;
    return this.cardsMoreReq;
  };

  @action fetchGeneralCards = async (id?: number) => {
    this.cardsGeneralReq = this.diagramApi.getGeneralCards(id, this.defaultEarTypes);
    await this.cardsGeneralReq;
    return this.cardsGeneralReq;
  };

  @action fetchDataBehavior = async (selectedJurisdictions: ItemType | null) => {
    if (selectedJurisdictions) {
      return this.selectingJurisdictions(selectedJurisdictions);
    } else {
      return this.fetchJurisdiction();
    }
  };

  @action fetchJurisdiction = async () => {
    await lookupsStore.fetchJurisdictionsLookups();
    runInAction(() => {
      if (lookupsStore.jurisdictions.length > 0) {
        this.selectingJurisdictions(lookupsStore.jurisdictions[0]);
      }
    });
  };

  @action fetchCards = async () => {
    await Promise.allSettled([
      this.fetchCommonCards(this.targetJurisdiction?.id),
      this.fetchGeneralCards(this.targetJurisdiction?.id),
      this.fetchMcrCards(this.targetJurisdiction?.id),
      this.fetchMoreCards(this.targetJurisdiction?.id),
    ]);
  };

  @action initDashboardFetching = async (selectedJurisdictions: ItemType | null, restoreFromStorage = false) => {
    this.resetFilterState();
    this.resetChartSelection();
    this.resetCardFilterTitle();
    this.selectedGeneratorTypesEAR = [];
    this.selectedGeneratorTypesKPI = [];
    
    // Reset or restore filter state before fetching
    if (restoreFromStorage) {
      this.applyStoredState();
    }

    await this.fetchDataBehavior(selectedJurisdictions);
  };

  @action applyStoredState = () => {
    const data = DashboardPersistenceManager.loadState();
    if (data) {
      // Only apply fields that are present in the persisted state
      if (data.filterSource !== undefined) {
        this.filterSource = data.filterSource;
      }
      if (data.selectedGeneratorTypesEAR !== undefined) {
        this.selectedGeneratorTypesEAR = data.selectedGeneratorTypesEAR;
      }
      if (data.selectedGeneratorTypesKPI !== undefined) {
        this.selectedGeneratorTypesKPI = data.selectedGeneratorTypesKPI;
      }
      if (data.selectedPolarChartSector !== undefined) {
        this.selectedPolarChartSector = data.selectedPolarChartSector;
      }

      if (data.complianceMode) {
        this.complianceMode = data.complianceMode;
      }

      if (data.selectedPolarChartSector) {
        this.filter.setFilterValue({
          'q.filter.id': data.selectedPolarChartSector,
        });
      }
    }
  };

  @action saveToStorage = () => {
    const state: DashboardPersistedState = {
      filterSource: this.filterSource,
      selectedGeneratorTypesEAR: this.selectedGeneratorTypesEAR,
      selectedGeneratorTypesKPI: this.selectedGeneratorTypesKPI,
      selectedPolarChartSector: this.selectedPolarChartSector,
      complianceMode: this.complianceMode,
    };
    DashboardPersistenceManager.saveState(state);
  };

  @action setSelectedPolarChartSector = (sectorId: string | null) => {
    this.selectedPolarChartSector = sectorId;
  };

  @action setComplianceMode = (mode: ComplianceModeType) => {
    this.complianceMode = mode;
    ({
      'compliant': this.fetchSlcpCompliancePieChart,
      'notCompliant': this.fetchPieMaterialKPI,
      'not-compliant-by-container': this.fetchGeneratorsNotCompliantByContainerPieChart
    } as Record<ComplianceModeType, () => Promise<void>>)[this.complianceMode]();
    this.saveToStorage();
  };

  @action toggleSection = (name: DashboardSection, force?: boolean) => {
    this.sections.toggleSection(name, force);
    this.saveToStorage();
  };

  @action collapseAllSections = (force?: boolean) => {
    this.sections.collapseAll(force);
    this.saveToStorage();
  };

  @action fetchLinerComplianceEAR = async () => {
    this.linerComplianceEARReq = this.diagramApi.getEARLineChart(this.targetJurisdiction?.id, this.defaultEarTypes);
    await this.linerComplianceEARReq;
    runInAction(() => {
      if (this.linerComplianceEARData) {
        this.stockChartOptions = new StockChart({
          ...this.linerComplianceEARData,
          title: 'Included in a Compliance Review',
        }, this.applyEarLineChartFilter, this.applyEarPointFilter, this.exportEarCallBack);
      }
    });
  };

  @action fetchPieCollectionSysEAR = async () => {
    const typeIds = this.isAllGeneratorTypesSelectedEAR ? undefined : this.selectedGeneratorTypesEAR;
    this.pieCollectionSysEARReq = this.diagramApi.getPieChart(
      this.targetJurisdiction?.id,
      this.filter.filterParams,
      typeIds,
    );
    await this.pieCollectionSysEARReq;
    runInAction(() => {
      if (this.pieCollectionSysEARData) {
        this.pieChartOptions = new PieChart({
          ...this.pieCollectionSysEARData,
          title: 'Collection Systems',
          values: [...this.pieCollectionSysEARData.values],
        }, this.applyEarPieChartFilter, this.getEarExportFile);
      }
    });
  };

  @action applyEarPieChartFilter: ClickOnLegendType = async (id, chartTargetName, chartTitle, pointAmmount) => {
    if (this.selectedPolarChartSector) {
      this.selectedPolarChartSector = null;
      this.saveToStorage();
    }
    this.setFilterSource('ear');
    this.applyFilter(id, chartTargetName, chartTitle, pointAmmount);
  };

  // EAR Line Chart handlers - always use default types (filterSource = null)
  @action applyEarLineChartFilter: ClickOnLegendType = async (id, chartTargetName, chartTitle, pointAmmount) => {
    this.setFilterSource(null);
    this.applyFilter(id, chartTargetName, chartTitle, pointAmmount);
  };

  @action applyEarPointFilter: ClickOnPointType = async (
    filterId,
    legendTitle,
    chartTitle,
    pointId,
    pointAmmount,
    date,
    pointTimestamp,
  ) => {
    this.setFilterSource(null);
    this.applyPointFilter(filterId, legendTitle, chartTitle, pointId, pointAmmount, date, pointTimestamp);
  };

  // KPI Line Chart handlers - use KPI filter (filterSource = 'kpi')
  @action applyKpiLineChartFilter: ClickOnLegendType = async (id, chartTargetName, chartTitle, pointAmmount) => {
    this.setFilterSource('kpi');
    this.applyFilter(id, chartTargetName, chartTitle, pointAmmount);
  };

  @action applyKpiPointFilter: ClickOnPointType = async (
    filterId,
    legendTitle,
    chartTitle,
    pointId,
    pointAmmount,
    date,
    pointTimestamp,
    isLastPoint,
    element
  ) => {
    if (isLastPoint) {
      this.compliantContainersPopoverElement = element;
      const typeIds = this.isAllGeneratorTypesSelectedKPI ? undefined : this.selectedGeneratorTypesKPI;
      this.compliantContainersFilterId = filterId;
      this.compliantContainersReq = this.diagramApi.getCompliantContainers(filterId!, typeIds);
      return;
    }

    this.setFilterSource('kpi');
    this.applyPointFilter(filterId, legendTitle, chartTitle, pointId, pointAmmount, date, pointTimestamp);
  };

  @action fetchRecyclingKPI = async () => {
    const typeIds = this.isAllGeneratorTypesSelectedKPI ? undefined : this.selectedGeneratorTypesKPI;
    this.recyclingKPIReq = this.diagramApi.getSLCPLineChart(
      this.targetJurisdiction?.id,
      this.filter.filterParams,
      typeIds,
    );
    await this.recyclingKPIReq;
    runInAction(() => {
      if (this.recyclingKPIData) {
        this.multiChartOptions = new MultiStockChart({
          ...this.recyclingKPIData,
          title: 'SLCP Compliance',
        }, this.applyKpiLineChartFilter, this.applyKpiPointFilter, this.exportComplianceCallBack);
      }
    });
  };

  @action fetchPieMaterialKPI = async () => {
    const typeIds = this.isAllGeneratorTypesSelectedKPI ? undefined : this.selectedGeneratorTypesKPI;
    this.pieMaterialKPIReq = this.diagramApi.getNonPieChart(
      this.targetJurisdiction?.id,
      this.filter.filterParams,
      typeIds,
    );
    await this.pieMaterialKPIReq;
    runInAction(() => {
      if (this.pieMaterialKPIData) {
        this.polarChartOptions = new PolarChart({
          ...this.pieMaterialKPIData,
          title: 'Generators NOT Compliant by Material Type',
        }, this.applyPolarChartFilter, this.getEarExportFile);
      }
    });
  };

  @action fetchSlcpCompliancePieChart = async () => {
    const typeIds = this.isAllGeneratorTypesSelectedKPI ? undefined : this.selectedGeneratorTypesKPI;
    this.pieSecondMaterialKPIReq = this.diagramApi.getSlcpCompliancePieChart(
      this.targetJurisdiction?.id,
      this.filter.filterParams,
      typeIds,
    );
    await this.pieSecondMaterialKPIReq;
    runInAction(() => {
      if (this.slcpCompliancePieChartData) {
        this.compliancePieChartOptions = new PieChart(
          {
            ...this.slcpCompliancePieChartData,
            title: 'SLCP Compliance',
            values: [...this.slcpCompliancePieChartData.values],
          },
          this.applyPolarChartFilter,
          this.exportCompliancePieChartCallBack,
          slcpCompliancePieColors,
        );
      }
    });
  };

  @action fetchGeneratorsNotCompliantByContainerPieChart = async () => {
    const typeIds = this.isAllGeneratorTypesSelectedKPI ? undefined : this.selectedGeneratorTypesKPI;
    this.pieGeneratorsNotCompliantByContainerPieChartReq = this.diagramApi.getGeneratorsNotCompliantByContainerPieChart(
      this.targetJurisdiction?.id,
      this.filter.filterParams,
      typeIds,
    );
    await this.pieGeneratorsNotCompliantByContainerPieChartReq;
    runInAction(() => {
      if (this.generatorsNotCompliantByContainerPieChartData) {
        this.generatorsNotCompliantByContainerPieChartOptions = new PieChart(
          {
            ...this.generatorsNotCompliantByContainerPieChartData,
            title: 'Generators NOT Compliant by Container',
            values: [...this.generatorsNotCompliantByContainerPieChartData.values],
          },
          this.applyPolarChartFilter,
          this.exportGeneratorsNotCompliantByContainerPieChart,
          generatorsNotCompliantByContainerPieColors,
        );
      }
    });
  };

  @action applyPolarChartFilter: ClickOnLegendType = async (id, chartTargetName, chartTitle, pointAmmount) => {
    const isDeselect = chartTargetName === null;
    if (isDeselect) {
      this.selectedPolarChartSector = null;
      this.filter.resetFilters();
      this.resetCardFilterTitle();
      this.selectedPointTimestamp = null;
      this.selectedFilterId = null;
      this.saveToStorage();
      this.fetchLocations();
      return;
    }
    this.selectedPolarChartSector = String(id);
    this.setFilterSource('kpi');
    this.applyFilter(id, chartTargetName, chartTitle, pointAmmount);
    this.saveToStorage();
  };

  @action fetchAllCharts = async () => {
    const chartFetches = [
      this.fetchLinerComplianceEAR(),
      this.fetchPieCollectionSysEAR(),
      this.fetchRecyclingKPI(),
      this.fetchPieMaterialKPI(),
    ];

    chartFetches.push(({
      'compliant': this.fetchSlcpCompliancePieChart,
      'notCompliant': this.fetchPieMaterialKPI,
      'not-compliant-by-container': this.fetchGeneratorsNotCompliantByContainerPieChart
    } as Record<ComplianceModeType, () => Promise<void>>)[this.complianceMode]());

    await Promise.allSettled(chartFetches);
  };

  @action fetchFilteredCollectionSysEAR = async () => {
    await this.fetchPieCollectionSysEAR();
    this.saveToStorage();
  };

  @action fetchFilteredKPICharts = async () => {
    await Promise.allSettled([
      this.fetchRecyclingKPI(),
      this.fetchPieMaterialKPI(),
    ]);
    this.saveToStorage();
  };

  @action exportEarCallBack: ExportCallBackType = ({ type }) => {
    this.getEarExportFile({
      dashboard: [exportOptions["SLCP EAR Compliance Review Line Chart"]],
      type,
    });
  };

  @action exportComplianceCallBack: ExportCallBackType = ({ type }) => {
    this.getEarExportFile({
      dashboard: [exportOptions["SLCP KPI's Compliance Line Chart"]],
      type,
    });
  };

  @action exportCompliancePieChartCallBack: ExportCallBackType = ({ type }) => {
    this.getEarExportFile({
      dashboard: [exportOptions["SLCP KPI's Compliance Pie Chart"]],
      type,
    });
  };

  @action exportGeneratorsNotCompliantByContainerPieChart: ExportCallBackType = ({ type }) => {
    this.getEarExportFile({
      dashboard: [exportOptions["SLCP KPI's Not Compliant by Container Pie Chart"]],
      type,
    });
  };

  @action applyFilter: ClickOnLegendType = async (id, chartTargetName, chartTitle, pointAmmount) => {
    this.filter.resetFilters();
    this.resetChartSelection();
    this.selectedPointTimestamp = null;
    this.selectedFilterId = String(id);

    this.fetchLegendFilter(
      String(id),
      chartTargetName,
      chartTitle,
      pointAmmount,
    );
  };
  @action applyPointFilter: ClickOnPointType = async (
    filterId,
    legendTitle,
    chartTitle,
    pointId,
    pointAmmount,
    date,
    pointTimestamp,
  ) => {
    this.filter.resetFilters();
    this.resetChartSelection();
    this.selectedFilterId = filterId;
    this.selectedPointTimestamp = pointTimestamp ?? null;

    this.filter.setFilterValue({
      'q.point.id': pointId ?? 'null',
    });

    this.fetchLegendFilter(
      filterId,
      legendTitle,
      chartTitle,
      pointAmmount,
      date,
    );
  };

  @action selectingJurisdictions = async (value: ItemType) => {
    this.resetAll();
    await this.setJurisdictions(value);
    await this.fetchCards();
    await this.fetchAllCharts();
    await this.selectAllGeneratorTypes();
    await this.fetchLocations();
  };

  @action selectingCards = (
    value: CardType['value'],
    title: CardType['title'],
    count?: string | number,
    filterSourceOverride?: FilterSourceType,
  ) => {
    this.filter.resetFilters();
    this.filterSource = filterSourceOverride ?? null;
    const label = !!count ? `${count} ${title}` : title;
    if (ignoreType(Number(value), [CardsType.RecoveryOS, CardsType.TotalPounds])) {
      this.filter.setFilterValue({
        'q.filter.id': value as string,
      });
      this.setCardFilterTitle(label);
      this.selectedPointTimestamp = null;
      this.selectedFilterId = value as string;

      (async () => {
        await this.resetChartSelection();
        await this.fetchLocations();
      })();
    }
  };

  // Centralized generator types for API requests (table, etc.)
  // - Default state (all selected): send defaultEarTypes ['1', '2']
  // - TopCards: no type filtering (return empty)
  // - Custom filter selection: send selected types
  @computed get requestGeneratorTypes(): string[] {
    // Default state - no filter applied
    if (!this.selectedFilterId) {
      return [];
    }
    // TopCards - no type filtering
    if (this.filterSource === 'topCards') {
      return [];
    }
    // EAR Line Chart (filterSource = null) - use default types
    if (!this.filterSource) {
      return this.defaultEarTypes;
    }
    // Custom selection - send selected types
    if (this.isAllGeneratorTypesSelected) {
      return [];
    }
    return this.selectedGeneratorTypes;
  }

  @action fetchLocations = async () => {
    const filters = this.filter.filterParams;
    const additionalFilters = this.generatorsAdditionalFilter;

    this.generatorsReq = this.diagramApi.getEarGeneratorsData({
      filters: filters,
      additionalFilters,
      ...this.pagination.serialize,
    });
    await this.generatorsReq;

    runInAction(() => {
      if (
        this.generatorsReq.state !== 'fulfilled' ||
        this.generatorsReq.value?.data === null
      )
        return;
      this.generators.replace(
        this.generatorsData.map(generator => {
          return new Generator(generator);
        }),
      );
      this.pagination.setPagination(this.generatorsReq.value?.data);
    });
    return this.generatorsReq;
  };

  @action changeJurisdictions = async (page: number) => {
    runInAction(() => {
      this.pagination.setPagination({
        pagination: {
          num: page + 1,
          size: this.pagination.serialize.pageSize,
        },
      });
    });
    await this.fetchLocations();
  };

  @action removeTargetGenerator = () => this.chosenGenerator = undefined;

  @action setSelectedGenerators = (items: GridRowId[]) => {
    this.selectedGenerators = items;
  };

  @action getEarExportFile = async ({ dashboard, type }: ExportDashboardFormType) => {
    if (!dashboard || dashboard.length === 0) return;

    const generatorTypeMapping: {[key: string]: string[]} = {
      'SlcpSubjectLineChart': this.defaultEarTypes,
      'SlcpPieChart': this.selectedGeneratorTypesEAR,
      'Cards': this.defaultEarTypes,
      'SlcpComplianceLineChart': this.selectedGeneratorTypesKPI,
      'SlcpNonPieChart': this.selectedGeneratorTypesKPI,
      'SlcpCompliancePieChart': this.selectedGeneratorTypesKPI,
      'MOReCards': this.defaultEarTypes,
      'McrCards': this.defaultEarTypes,
      'SlcpNonComplianceContainersPieChart': this.selectedGeneratorTypesKPI,
    }

    for (const { path, name } of dashboard) {
      this.dashboardExportEntity.setParams({
        fileName: name,
        type: type,
        mode: ExportTypes.ALL,
      });
      const baseExportParams = this.dashboardExportEntity.exportParams([], null, this.pagination, []);

      const generatorTypes = generatorTypeMapping[path] ?? [];
      const rawEarParams = {
        ...dotNameKeyToObject(this.filter.filterParams),
        ...(generatorTypes.length > 0 && {
          typeIds: generatorTypes.map(x => Number(x)),
        }),
      };
      const earParams = removeEmptyValues(rawEarParams);
      const exportParams = {
        ...baseExportParams,
        q: {
          ...baseExportParams.q,
          ...(this.selectedPointTimestamp && { pointTimestamp: this.selectedPointTimestamp }),
          ...earParams,
        },
      };
      await this.dashboardExportEntity.asyncExportAction(
        this.diagramApi.getAsyncDashboardChartByType(
          path,
          type,
          exportParams,
        ),
      );
      runInAction(() => {
        if (this.dashboardExportEntity.exportReq.state === 'fulfilled' && this.dashboardExportEntity.isExport) {
          this.dashboardExportEntity.resetParams();
        }
      });
    }
  };

  @action exportAction = async (data: ExportDataType) => {
    if (!data.type) return;
    if (Number(data.mode) === ExportTypes.ALL) this.exportEntity.setExportWithUnactiveRecords(true);
    this.exportEntity.setParams(data);

    let additionalFilters = this.generatorsAdditionalFilter;

    const exportParams = this.exportEntity.exportParams<
      GeneratorsResponse, GeneratorsFilterType, GeneratorItem
    >(
      this.columnsManager.hiddenColumns.map(
        el => GeneratorExportColumnsNameMap[el as keyof GeneratorItem] || el,
      ),
      this.filter,
      this.pagination,
      this.selectedGenerators,
    );

    if (Number(data.mode) === ExportTypes.ALL) {
      additionalFilters = null;
    }

    await this.exportEntity.asyncExportAction(
      this.diagramApi.getAsyncGeneratorsReportByType(
        exportParams,
        additionalFilters,
        data.type,
      ),
    );
  };

  @action fetchLegendFilter: ClickOnLegendType = (filterId, legendTitle, chartTitle, pointAmmount, _date) => {
    if (!legendTitle) return;

    this.filter.setFilterValue({
      'q.filter.id': filterId,
    });
    this.setCardFilterTitle(generateFilterTitle(chartTitle || '', legendTitle, pointAmmount ?? undefined));
    (async () => await this.fetchLocations())();
  };

  @action resetFilterState = () => {
    this.filter.resetFilters();
    this.filterSource = null;
    this.selectedPolarChartSector = null;
    this.selectedPointTimestamp = null;
    this.selectedFilterId = null;
  };

  // Clear all filters and refresh charts - called from "Clear filters" button
  @action clearFilters = async () => {
    this.resetFilterState();
    this.resetChartSelection();
    this.resetCardFilterTitle();

    await this.fetchLocations();
  };

  @action resetStore = () => {
    this.pagination.resetPagination();
    this.filter.setSearchOpen(false);
    this.removeSelectedGenerators();
  };


  @action resetChartEntity = () => {
    this.multiChartOptions = undefined;
    this.stockChartOptions = undefined;
    this.pieChartOptions = undefined;
    this.polarChartOptions = undefined;
    this.compliancePieChartOptions = undefined;
    this.generatorsNotCompliantByContainerPieChartOptions = undefined;
  };

  @action resetCardFilterTitle = () => this.cardFilterTitle = undefined;
  @action removeSelectedGenerators = () => this.selectedGenerators = [];

  @action resetPieChartSelection = () => {
    const pieCharts = [
      this.pieChartOptions,
      this.polarChartOptions,
      this.compliancePieChartOptions,
      this.generatorsNotCompliantByContainerPieChartOptions
    ];
    for (let pieChart of pieCharts) {
      if (pieChart?.chartRef) {
        pieChart.resetSelection();
      }
    }
  };
  @action resetLineChartSelection = () => {
    if (this.stockChartOptions?.chartRef) {
      this.stockChartOptions.resetSelection();
    }
    if (this.multiChartOptions?.chartRef) {
      this.multiChartOptions.resetSelection();
    }
  };
  @action resetChartSelection = () => {
    this.resetPieChartSelection();
    this.resetLineChartSelection();
  };
  @action resetAllChart = () => {
    this.resetPieChartSelection();
    this.resetLineChartSelection();
    this.resetCardFilterTitle();
    this.resetChartEntity(); //position is important
  };

  @action resetAll = () => {
    this.resetAllChart();
    this.resetStore();
  };

  @action clearCompliantContainersPopoverElement = () => {
    this.compliantContainersPopoverElement = undefined;
  }
}


export const { store: dashboardStore, storeCtx: dashboardStoreCtx } = storeFactory(
  DashboardStore,
  'dashboard',
);

